import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Search, SearchX } from "lucide-react";
import PageHero from "@/components/PageHero";
import ShowCard from "@/components/ShowCard";
import ShowModal from "@/components/ShowModal";
import { SHOWS, CATEGORIES } from "@/data/content";

export default function Shows() {
    const [category, setCategory] = useState("All");
    const [query, setQuery] = useState("");
    const [selected, setSelected] = useState(null);

    const q = query.trim().toLowerCase();
    const filtered = SHOWS.filter(
        (s) =>
            (category === "All" || s.category === category) &&
            (q === "" || `${s.title} ${s.category} ${s.desc}`.toLowerCase().includes(q))
    );

    return (
        <div data-testid="shows-page">
            <PageHero
                kicker="Shows & Programming"
                title="Entertainment for every"
                accent="mood, age & moment"
                sub="Twelve original programmes across lifestyle, music, children's favourites, movies and prime-time entertainment."
                crumb="Shows & Programming"
            />

            <section className="bg-[#0F0F23] pb-24 sm:pb-32">
                <div className="mx-auto max-w-7xl px-4 sm:px-6">
                    <div className="relative mx-auto max-w-2xl" data-testid="show-search">
                        <Search size={18} className="pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 text-[#9999BB]" />
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search any programme — try 'kirtan', 'movies', 'quiz'…"
                            data-testid="show-search-input"
                            className="w-full rounded-full border border-white/10 bg-[#16162B] py-4 pl-12 pr-5 text-sm text-white placeholder-[#666688] outline-none transition-colors focus:border-[#C8102E]"
                        />
                    </div>

                    <div className="sticky top-20 z-30 -mx-4 mt-8 flex gap-2 overflow-x-auto bg-[#0F0F23]/95 px-4 py-4 backdrop-blur-xl sm:mx-0 sm:flex-wrap sm:px-0" data-testid="category-filter">
                        {CATEGORIES.map((c) => (
                            <button
                                key={c}
                                type="button"
                                data-testid={`filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                onClick={() => setCategory(c)}
                                className={`relative shrink-0 rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-[0.15em] transition-colors duration-300 ${
                                    category === c ? "text-white" : "text-[#9999BB] hover:text-white"
                                }`}
                            >
                                {category === c && (
                                    <motion.span
                                        layoutId="filter-pill"
                                        className="absolute inset-0 rounded-full bg-[#C8102E]"
                                        transition={{ type: "spring", stiffness: 400, damping: 32 }}
                                    />
                                )}
                                <span className="relative z-10">{c}</span>
                            </button>
                        ))}
                    </div>

                    {filtered.length === 0 ? (
                        <div className="mt-16 flex flex-col items-center gap-4 text-center" data-testid="shows-empty">
                            <SearchX size={40} className="text-[#C8102E]" />
                            <p className="font-display text-xl font-bold text-white">No programmes found</p>
                            <p className="text-sm text-[#9999BB]">Try a different search or category.</p>
                        </div>
                    ) : (
                        <motion.div layout className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="shows-grid">
                            <AnimatePresence mode="popLayout">
                                {filtered.map((show, i) => (
                                    <motion.div
                                        key={show.slug}
                                        layout
                                        initial={{ opacity: 0, scale: 0.92 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.92 }}
                                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                                    >
                                        <ShowCard show={show} index={i} onOpen={setSelected} />
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </motion.div>
                    )}
                </div>
            </section>

            <ShowModal show={selected} onClose={() => setSelected(null)} />
        </div>
    );
}
